import { graphql, useStaticQuery } from 'gatsby';
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews';
import { useLocation } from '@reach/router';

import defaultLogo from 'assets/others/logo.png';
import { INTERNAL_LINKS } from 'utils/constants';

const useDefaults = (pathname: string) => {
  const staticSEOMetadata = useStaticQuery<Queries.SEOMetadataQuery>(graphql`
    query SEOMetadata {
      site {
        siteMetadata {
          title
          url
          description
          titleTemplate
        }
      }
    }
  `);

  const {
    data: { site },
  } = useMergePrismicPreviewData(staticSEOMetadata);

  const {
    url: defaultUrl = '',
    title = '',
    titleTemplate = '',
    description = '',
  } = site?.siteMetadata || {};

  const url =
    defaultUrl === '' && typeof window !== 'undefined' ? window.location.origin : defaultUrl;

  if (url === '') {
    // eslint-disable-next-line no-console
    console.error('Please set a baseUrl in your site metadata');
  }

  return {
    url,
    title,
    description,
    templateTitle: (pageTitle = title) =>
      pathname === INTERNAL_LINKS.HOME
        ? titleTemplate?.replace(/%s/g, pageTitle as string)
        : pageTitle,
  };
};

export const useSEO = (data: Queries.SEODataFragment['data']) => {
  const { pathname } = useLocation();

  const { url, title, description, templateTitle } = useDefaults(pathname);

  if (!data?.seo?.[0]?.primary) return null;

  const [
    {
      primary: {
        page_title = title,
        og_title = page_title,
        twitter_title = page_title,
        page_description = description,
        og_description = page_description,
        twitter_description = page_description,
        page_image,
        og_image,
        twitter_image,
      },
    },
  ] = data.seo;

  const image = {
    url: page_image?.url ?? defaultLogo,
    alt: page_image?.alt ?? 'Logo',
  };

  const ogImage = {
    url: og_image?.url ?? image.url,
    alt: og_image?.alt ?? image.alt,
  };

  const twitterImage = {
    url: twitter_image?.url ?? image.url,
    alt: twitter_image?.alt ?? image.alt,
  };

  return {
    url: `${url}${pathname}`,
    title: templateTitle(page_title),
    ogTitle: templateTitle(og_title),
    twitterTitle: templateTitle(twitter_title),
    description: page_description,
    ogDescription: og_description,
    twitterDescription: twitter_description,
    image: image,
    ogImage,
    twitterImage,
  };
};
